import ConfirmDialog from 'components/ConfirmDialog'
import PPSelect, { PPSelectOptions } from 'components/PPSelect'
import { Fragment, useEffect, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { toggleGenericStatus } from 'services/compose'
import {
  activePostIdSelector,
  composerActiveTabSelector,
  composerBulkFileSelector,
  composerIsEmptySelector,
  ComposerPostStatus,
  composerPostStatusByNetworkSelector,
  composerResetKeySelector,
  composerTabsSelector,
  genericPostSelector,
  multiPostsCountSelector,
  useGenericStatusSelector
} from 'services/compose/selectors'
import { sortNetworksDefault } from 'shared/utils'
import Accordion from '../Accordion'
import BulkUploadNetworkSettings from '../BulkUploadNetworkSettings/BulkUploadNetworkSettings'
import BulkPostsManager from '../BulkView/components/BulkPostsManager'
import Media from '../Content/Media'
import styles from './Network.pcss'
import Optimize from './Optimize'

export function Network(props: { activeView: 'default' | 'bulk' }) {
  const isDefaultView = props.activeView === 'default'
  const dispatch = useDispatch()
  const bulkUploadFile = useSelector(composerBulkFileSelector)
  const multiPostsCount = useSelector(multiPostsCountSelector)
  const activeTab = useSelector(composerActiveTabSelector)
  const isEditMode = Boolean(useSelector(activePostIdSelector))
  const networks = useSelector(composerTabsSelector)
  const ordered = networks.sort(sortNetworksDefault)
  const statusByNetwork: Record<string, ComposerPostStatus> = useSelector(composerPostStatusByNetworkSelector)
  const isEmpty = useSelector(composerIsEmptySelector)
  const useGenericStatus = useSelector(useGenericStatusSelector)
  const genericStatus = useSelector(genericPostSelector).status.text
  const resetKey = useSelector(composerResetKeySelector)
  const [isOpen, setIsOpen] = useState(isEmpty)
  const [captionSwitchVisible, setCaptionSwitchVisible] = useState(false)

  const isUsingGenericStatus = useRef(useGenericStatus)

  useEffect(() => {
    setCaptionSwitchVisible(false)
  }, [resetKey])

  useEffect(() => {
    isUsingGenericStatus.current = useGenericStatus
  }, [useGenericStatus])

  useEffect(() => {
    if (genericStatus && !isUsingGenericStatus.current) {
      setCaptionSwitchVisible(true)
    }
  }, [genericStatus])

  const onSectionExpandedToggle = (nextValue: boolean) => {
    setIsOpen(nextValue)
  }

  useEffect(() => {
    setIsOpen(isEditMode || !isDefaultView)
  }, [resetKey, isDefaultView, isEditMode])

  useEffect(() => {
    if (!isEmpty) {
      setIsOpen(true)
    }
  }, [isEmpty])

  const captionSwitchOptions: PPSelectOptions = {
    generic: { label: <FormattedMessage id="composer.labels.switch-caption.generic" /> },
    custom: { label: <FormattedMessage id="composer.labels.switch-caption.custom" /> }
  }

  const onToggleCaptionSwitch = () => {
    dispatch(toggleGenericStatus())
    setIsOpen(true)
  }

  // NOTE: Do not render empty section in bulk mode
  if (!isDefaultView && !bulkUploadFile && multiPostsCount === 0) {
    return null
  }

  return (
    <Accordion
      key={resetKey}
      title={<FormattedMessage id={isEditMode ? 'composer.titles.post-edit' : 'composer.titles.optimize'} />}
      order={isEditMode ? '1' : '3'}
      open={isOpen}
      id={isEditMode ? 'composer-optimize-edit' : 'composer-optimize'}
      contentClassName={styles['acc-content']}
      headerElements={(
        <div className={styles['header-content']}>
          {!bulkUploadFile && !isEditMode && Boolean(genericStatus) && !useGenericStatus && captionSwitchVisible && (
            <div className={styles['switch-box']}>
              <ConfirmDialog
                message={(
                  <div>
                    <h3><FormattedMessage id="composer.prompt.reset-caption" /></h3>
                    <div><FormattedMessage id="composer.prompt.reset-caption-subtitle" /></div>
                  </div>
                )}
                confirmLabel={<FormattedMessage id="actions.reset" />}
              >
                {(confirm) => (
                  <PPSelect
                    name={<FormattedMessage id="label.generic.caption" />}
                    options={captionSwitchOptions}
                    selectedValue={useGenericStatus ? 'generic' : 'custom'}
                    withCaret
                    className={`${styles.switch} ${useGenericStatus ? '' : styles.highlight}`}
                    onSelectionChange={!useGenericStatus ? confirm(onToggleCaptionSwitch) : onToggleCaptionSwitch}
                  />
                )}
              </ConfirmDialog>
            </div>
          )}
        </div>
      )}
      onToggle={onSectionExpandedToggle}
    >
      {isDefaultView ? (
        <div>
          {ordered.map(network => (
            <Optimize key={network} network={network} contentElement={isEditMode && <Media />} />
          ))}
        </div>
      ) : (
        <Fragment>
          {multiPostsCount > 0 && (
            <BulkPostsManager />
          )}
          {bulkUploadFile && (
            <BulkUploadNetworkSettings />
          )}
        </Fragment>
      )}
    </Accordion>
  )
}

export default Network
